/* =========================================================================
   Shared primitives for the redesigned Work Orders & Change Orders section.
   Ported from docs/featureBriefs/design_handoff_work_orders_change_orders/prototype/app/work-orders.js.
   All markup relies on classes in WorkOrders.css, scoped under `.wov2`.
   ========================================================================= */
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    AlertTriangle,
    Ban,
    Check,
    CheckCircle2,
    ChevronLeft,
    Clock,
    DollarSign,
    Inbox,
    PenLine,
    XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { WorkOrderListItem } from "../../../interfaces/WorkOrderInterface";

/* ---------------------------------------------------------------- status systems */

export interface StatusMeta {
    name: string;
    short: string;
    note: string;
    /** stage color class st1..st6 (accent bars, dots, timeline) */
    stClass: string;
    /** status pill class */
    pillClass: string;
    color: string;
    Icon: LucideIcon;
}

export const WO_STATUS_META: Record<number, StatusMeta> = {
    1: { name: "Pending Field Signature", short: "Pending Signature", note: "waiting on customer", stClass: "st1", pillClass: "st-sign", color: "#e08a2b", Icon: PenLine },
    2: { name: "Pending PO", short: "Pending PO", note: "waiting on builder", stClass: "st2", pillClass: "st-po", color: "#7a5cf0", Icon: Clock },
    3: { name: "Received PO", short: "Received PO", note: "ready to invoice", stClass: "st3", pillClass: "st-recv", color: "#2f6fed", Icon: Inbox },
    4: { name: "Invoiced", short: "Invoiced", note: "closed", stClass: "st4", pillClass: "st-inv", color: "#1f9d6b", Icon: DollarSign },
    5: { name: "Voided", short: "Voided", note: "", stClass: "st5", pillClass: "st-void", color: "#e0533f", Icon: XCircle },
    6: { name: "No Charge", short: "No Charge", note: "", stClass: "st6", pillClass: "st-nc", color: "#8a93a2", Icon: Ban },
};

/** CO statuses reuse the WO stage palette: Open→blue, Completed→green, Voided→red, No Charge→gray. */
export const CO_STATUS_META: Record<number, StatusMeta> = {
    1: { name: "Open", short: "Open", note: "", stClass: "st3", pillClass: "st-open", color: "#2f6fed", Icon: Clock },
    2: { name: "Completed", short: "Completed", note: "", stClass: "st4", pillClass: "st-inv", color: "#1f9d6b", Icon: CheckCircle2 },
    3: { name: "Voided", short: "Voided", note: "", stClass: "st5", pillClass: "st-void", color: "#e0533f", Icon: XCircle },
    4: { name: "No Charge", short: "No Charge", note: "", stClass: "st6", pillClass: "st-nc", color: "#8a93a2", Icon: Ban },
};

export const TRADE_CHIP_CLASS: Record<string, string> = {
    Drywall: "tr-drywall",
    Paint: "tr-paint",
    Other: "tr-other",
};

/* ---------------------------------------------------------------- formatting */

export function money(n: number | null | undefined): string {
    return "$" + (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** $1,234 — drops cents when they're .00 (flow-board sub lines) */
export function moneyCompact(n: number | null | undefined): string {
    return money(n).replace(/\.00$/, "");
}

export function fmtDate(iso: string | null | undefined): string {
    if (!iso) return "–";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "–";
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

export function fmtDateTime(iso: string | null | undefined): string {
    if (!iso) return "–";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "–";
    return `${fmtDate(iso)} ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
}

export function ageDays(iso: string): number {
    return Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 86400000));
}

/** Business WO number, or the internal id in the distinct raw-id style. */
export function WoNum({ wo }: { wo: Pick<WorkOrderListItem, "altWorkOrderNumber" | "workOrderID"> }) {
    return wo.altWorkOrderNumber
        ? <span className="code">WO #{wo.altWorkOrderNumber}</span>
        : <span className="raw-id">{wo.workOrderID}</span>;
}

export function woNumText(wo: Pick<WorkOrderListItem, "altWorkOrderNumber" | "workOrderID">): string {
    return wo.altWorkOrderNumber ? `WO #${wo.altWorkOrderNumber}` : String(wo.workOrderID);
}

/* ---------------------------------------------------------------- small pieces */

export function StatusPill({ kind, status }: { kind: "wo" | "co"; status: number }) {
    const meta = (kind === "wo" ? WO_STATUS_META : CO_STATUS_META)[status];
    if (!meta) return null;
    const { Icon } = meta;
    return <span className={`pill ${meta.pillClass}`}><Icon size={12} />{meta.short}</span>;
}

export function TradeChip({ trade }: { trade: string }) {
    return <span className={`chip ${TRADE_CHIP_CLASS[trade] || "tr-other"}`}>{trade}</span>;
}

/** `Nd` age pill — warm at ≥10 days, hot at ≥21. */
export function AgePill({ createdAt }: { createdAt: string }) {
    const d = ageDays(createdAt);
    const cls = d >= 21 ? "hot" : d >= 10 ? "warm" : "";
    return <span className={`age ${cls}`}>{d}d</span>;
}

export function Ck({ on, onClick, title }: { on: boolean; onClick: (e: React.MouseEvent) => void; title?: string }) {
    return <span className={`ck ${on ? "on" : ""}`} onClick={onClick} title={title}><Check size={12} /></span>;
}

export function PageHead({ Icon, title, sub, actions }: { Icon: LucideIcon; title: React.ReactNode; sub: React.ReactNode; actions?: React.ReactNode }) {
    return (
        <div className="page-head">
            <div className="ph-ic"><Icon /></div>
            <div className="grow">
                <h1>{title}</h1>
                <div className="ph-sub">{sub}</div>
            </div>
            {actions}
        </div>
    );
}

export function SectionLabel({ Icon, children, style }: { Icon: LucideIcon; children: React.ReactNode; style?: React.CSSProperties }) {
    return <div className="section-label" style={style}><Icon size={13} />{children}</div>;
}

export function BackBar({ label, onBack, crumb }: { label: string; onBack: () => void; crumb?: React.ReactNode }) {
    return (
        <div className="backbar">
            <button className="backbtn" onClick={onBack}><ChevronLeft size={14} />{label}</button>
            {crumb && <span className="crumb-mini">{crumb}</span>}
        </div>
    );
}

export function EmptyState({ Icon, title, children, style }: { Icon: LucideIcon; title?: string; children?: React.ReactNode; style?: React.CSSProperties }) {
    return (
        <div className="empty" style={style}>
            <div className="eic"><Icon /></div>
            {title && <div className="et">{title}</div>}
            {children && <p>{children}</p>}
        </div>
    );
}

export function Kv({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
    return <div className={`kv ${className || ""}`}><div className="k">{label}</div><div className="v">{children}</div></div>;
}

export function Banner({ variant, Icon, children, action }: { variant: "danger" | "neutral" | "ok" | "warn" | "info"; Icon: LucideIcon; children: React.ReactNode; action?: React.ReactNode }) {
    return (
        <div className={`banner ${variant}`}>
            <Icon size={16} />
            <span className="grow">{children}</span>
            {action}
        </div>
    );
}

export function DisabledNote({ children }: { children: React.ReactNode }) {
    return <div className="disabled-note"><AlertTriangle size={13} />{children}</div>;
}

/** The completion gate banner (CO detail) / any visible lock explanation. */
export function Gate({ children }: { children: React.ReactNode }) {
    return <div className="gate"><AlertTriangle size={16} /><span>{children}</span></div>;
}

/* ---------------------------------------------------------------- timeline */

export interface TimelineEntry {
    key: string | number;
    stClass: string;
    Icon: LucideIcon;
    title: string;
    note?: string | null;
    when: string;
    by?: string | null;
}

export function Timeline({ entries }: { entries: TimelineEntry[] }) {
    if (!entries.length) return <EmptyState Icon={Clock} style={{ padding: 24 }}>No history yet.</EmptyState>;
    return (
        <div className="timeline">
            {entries.map((e) => (
                <div className="tl" key={e.key}>
                    <span className={`dot ${e.stClass}`}><e.Icon size={11} /></span>
                    <div className="tlt">{e.title}</div>
                    {e.note ? <div className="tlm">{e.note}</div> : null}
                    <div className="tld">{e.when}{e.by ? ` · ${e.by}` : ""}</div>
                </div>
            ))}
        </div>
    );
}

/* ---------------------------------------------------------------- documents */

export function DocRow({ Icon, iconVariant, name, meta, action }: { Icon: LucideIcon; iconVariant?: "ok"; name: React.ReactNode; meta: React.ReactNode; action?: React.ReactNode }) {
    return (
        <div className="docrow">
            <div className={`di ${iconVariant || ""}`}><Icon size={15} /></div>
            <div className="grow">
                <div className="dt-name">{name}</div>
                <div className="dt-meta">{meta}</div>
            </div>
            {action}
        </div>
    );
}

/* ---------------------------------------------------------------- overflow menu */

export type OvfItem = { label: string; Icon: LucideIcon; danger?: boolean; onClick: () => void } | "sep";

export function OverflowMenu({ items, ButtonIcon }: { items: OvfItem[]; ButtonIcon: LucideIcon }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [open]);
    return (
        <div className="ovf" ref={ref}>
            <button className="btn icon" title="More actions" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                <ButtonIcon size={16} />
            </button>
            <div className={`ovf-menu ${open ? "open" : ""}`}>
                {items.map((it, i) =>
                    it === "sep"
                        ? <div className="ovf-sep" key={i} />
                        : <button key={i} className={it.danger ? "danger" : ""} onClick={() => { setOpen(false); it.onClick(); }}><it.Icon size={14} />{it.label}</button>
                )}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- modal shell */

export function ModalShell({ Icon, danger, title, sub, onClose, footer, children, wide }: {
    Icon: LucideIcon;
    danger?: boolean;
    title: string;
    sub?: React.ReactNode;
    onClose: () => void;
    footer?: React.ReactNode;
    children: React.ReactNode;
    wide?: boolean;
}) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className="modal-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={`modal ${wide ? "wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
                <div className="modal-head">
                    <span className={`mi ${danger ? "danger" : ""}`}><Icon size={16} /></span>
                    <div>
                        <h3>{title}</h3>
                        {sub && <div className="ms">{sub}</div>}
                    </div>
                    <button className="x" onClick={onClose} aria-label="Close"><XCircle size={16} /></button>
                </div>
                <div className="modal-body">{children}</div>
                {footer && <div className="modal-foot">{footer}</div>}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------------- toast */

export function useToast(): { toastNode: React.ReactNode; showToast: (msg: string) => void } {
    const [msg, setMsg] = useState<string | null>(null);
    const [visible, setVisible] = useState(false);
    const timerRef = useRef<number | undefined>(undefined);
    const showToast = useCallback((m: string) => {
        setMsg(m);
        setVisible(true);
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setVisible(false), 2600);
    }, []);
    useEffect(() => () => window.clearTimeout(timerRef.current), []);
    const toastNode = msg ? (
        <div className={`wov2-toast ${visible ? "show" : ""}`}><CheckCircle2 size={16} />{msg}</div>
    ) : null;
    return { toastNode, showToast };
}

/* ---------------------------------------------------------------- multi-select */

export interface MultiSelectOption { id: number | string; name: string }

export function MultiSelect({ Icon, label, options, selected, onToggle }: {
    Icon: LucideIcon;
    label: string;
    options: MultiSelectOption[];
    selected: (number | string)[];
    onToggle: (id: number | string) => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, [open]);
    return (
        <div className="msel" ref={ref}>
            <button className="msel-btn" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                <Icon size={14} />{label}
                {selected.length > 0 && <span className="b">{selected.length}</span>}
            </button>
            <div className={`msel-pop ${open ? "open" : ""}`}>
                {options.map((o) => (
                    <div className="msel-opt" key={o.id} onClick={(e) => { e.stopPropagation(); onToggle(o.id); }}>
                        <span className={`ck ${selected.includes(o.id) ? "on" : ""}`}><Check size={12} /></span>
                        {o.name}
                    </div>
                ))}
            </div>
        </div>
    );
}
